import type { DatabaseDocumentData, DocumentCategory, DocumentItemStatus, DocumentMediaType } from '@troia-v3/types';

export interface ArticleFormState {
  title: string;
  content: string;
  summary: string;
  category: string;
  author: string;
  itemStatus: string;
  tags: string[];
  keywords: string[];
  language: string;
  /** '' = sem mídia */
  mediaType: '' | DocumentMediaType;
  mediaUrl: string;
  coverImage: string;
  series: string;
  /** Texto do input numérico; vira número só no payload */
  seriesOrder: string;
  /** Segundos, lido do player na prévia; null = desconhecido */
  duration: number | null;
}

export const defaultArticleForm: ArticleFormState = {
  title: '',
  content: '',
  summary: '',
  category: 'article',
  author: '',
  itemStatus: 'draft',
  tags: [],
  keywords: [],
  language: 'pt-BR',
  mediaType: '',
  mediaUrl: '',
  coverImage: '',
  series: '',
  seriesOrder: '',
  duration: null,
};

export function formFromDocument(doc: DatabaseDocumentData): ArticleFormState {
  return {
    title: doc.title || '',
    content: doc.content || '',
    summary: doc.summary || '',
    category: doc.category || 'article',
    author: doc.author || '',
    itemStatus: doc.itemStatus || 'draft',
    tags: doc.tags || [],
    keywords: doc.keywords || [],
    language: doc.language || 'pt-BR',
    mediaType: doc.mediaType ?? '',
    mediaUrl: doc.mediaUrl ?? '',
    coverImage: doc.coverImage ?? '',
    series: doc.series ?? '',
    seriesOrder: doc.seriesOrder !== undefined ? String(doc.seriesOrder) : '',
    duration: doc.duration ?? null,
  };
}

const orUndefined = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);

/** Monta o `data` do documento. Campo ausente aqui = campo que nunca chega ao banco. */
export function buildArticlePayload(form: ArticleFormState): DatabaseDocumentData {
  const payload: DatabaseDocumentData = {
    title: form.title.trim(),
    content: form.content,
    summary: orUndefined(form.summary),
    category: form.category as DocumentCategory,
    author: orUndefined(form.author),
    itemStatus: form.itemStatus as DocumentItemStatus,
    tags: form.tags.length > 0 ? form.tags : undefined,
    keywords: form.keywords.length > 0 ? form.keywords : undefined,
    language: form.language || undefined,
  };

  if (form.mediaType) {
    payload.mediaType = form.mediaType;
    payload.mediaUrl = form.mediaUrl.trim();
  }
  const coverImage = orUndefined(form.coverImage);
  if (coverImage) payload.coverImage = coverImage;
  const series = orUndefined(form.series);
  if (series) payload.series = series;
  const seriesOrder = Number.parseInt(form.seriesOrder, 10);
  if (Number.isInteger(seriesOrder) && seriesOrder >= 0) payload.seriesOrder = seriesOrder;
  if (form.duration !== null && Number.isFinite(form.duration) && form.duration > 0) payload.duration = Math.round(form.duration);

  return payload;
}

/**
 * Campos de `DatabaseDocumentData` que o formulário NÃO edita; num update eles
 * voltam do documento existente para o PUT (que substitui `data` inteiro) não
 * apagá-los.
 */
export const UNMANAGED_ARTICLE_FIELDS = [
  'slug',
  'attachments',
  'externalId',
  'externalMetadata',
  'relatedDocuments',
  'embeddings',
  'customFields',
] as const;

export function preserveUnmanagedFields(
  existing: DatabaseDocumentData | undefined,
  payload: DatabaseDocumentData,
): DatabaseDocumentData {
  if (!existing) return payload;

  const result: DatabaseDocumentData = { ...payload };
  for (const field of UNMANAGED_ARTICLE_FIELDS) {
    if (field in result) continue;
    const existingValue = existing[field];
    if (existingValue === undefined) continue;
    (result as unknown as Record<string, unknown>)[field] = existingValue;
  }
  return result;
}
